/**
 * One-click IDE onboarding for ACM MCP.
 *
 * Token is just a key bound to the user who minted it — not to an LLM.
 * These helpers only format the same key for VS Code, VSCodium, Antigravity
 * and Cursor (deeplink + config snippet + onboarding markdown).
 */

export const IDE_CLIENTS = ['vscode', 'vscodium', 'antigravity', 'cursor'] as const;
export type IdeClient = (typeof IDE_CLIENTS)[number];

/** Values the connect APIs already accept, plus the new IDE ids. */
export type TokenClientType =
  | IdeClient
  | 'generic'
  | 'claude-code'
  | 'claude-desktop';

export interface IdeMeta {
  id: IdeClient;
  label: string;
  /** Where the user pastes JSON if the deep-link does not fire. */
  configPath: string;
  defaultLabel: string;
}

export const IDE_META: Record<IdeClient, IdeMeta> = {
  vscode: {
    id: 'vscode',
    label: 'VS Code',
    configPath: '.vscode/mcp.json',
    defaultLabel: 'VS Code',
  },
  vscodium: {
    id: 'vscodium',
    label: 'VSCodium',
    configPath: '.vscode/mcp.json',
    defaultLabel: 'VSCodium',
  },
  antigravity: {
    id: 'antigravity',
    label: 'Antigravity',
    configPath: '~/.gemini/antigravity/mcp_config.json',
    defaultLabel: 'Antigravity',
  },
  cursor: {
    id: 'cursor',
    label: 'Cursor',
    configPath: '~/.cursor/mcp.json',
    defaultLabel: 'Cursor',
  },
};

export function isIdeClient(v: string): v is IdeClient {
  return (IDE_CLIENTS as readonly string[]).includes(v);
}

function vscodeInstallPayload(name: string, mcpUrl: string, key: string) {
  return {
    name,
    type: 'http',
    url: mcpUrl,
    headers: { 'x-api-key': key },
  };
}

/** Official VS Code MCP install URL. VSCodium / Antigravity use the same payload. */
function vscodeFamilyDeeplink(
  protocol: 'vscode' | 'vscodium' | 'antigravity',
  name: string,
  mcpUrl: string,
  key: string,
): string {
  return `${protocol}:mcp/install?${encodeURIComponent(
    JSON.stringify(vscodeInstallPayload(name, mcpUrl, key)),
  )}`;
}

export function buildCursorDeeplink(mcpUrl: string, key: string, name = 'acm'): string {
  const config = btoa(JSON.stringify({ url: mcpUrl, headers: { 'x-api-key': key } }));
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(name)}&config=${encodeURIComponent(config)}`;
}

export function buildDeeplink(
  client: IdeClient,
  mcpUrl: string,
  key: string,
  serverName = 'acm',
): string {
  switch (client) {
    case 'vscode':
      return vscodeFamilyDeeplink('vscode', serverName, mcpUrl, key);
    case 'vscodium':
      return vscodeFamilyDeeplink('vscodium', serverName, mcpUrl, key);
    case 'antigravity':
      return vscodeFamilyDeeplink('antigravity', serverName, mcpUrl, key);
    case 'cursor':
      return buildCursorDeeplink(mcpUrl, key, serverName);
  }
}

export function buildSnippet(
  client: TokenClientType,
  mcpUrl: string,
  key: string,
  serverName = 'acm',
): string {
  const httpServer = { type: 'http', url: mcpUrl, headers: { 'x-api-key': key } };
  const cursorServer = { url: mcpUrl, headers: { 'x-api-key': key } };
  switch (client) {
    case 'vscode':
    case 'vscodium':
      return JSON.stringify({ servers: { [serverName]: httpServer } }, null, 2);
    case 'antigravity':
      return JSON.stringify(
        { mcpServers: { [serverName]: { serverUrl: mcpUrl, headers: { 'x-api-key': key } } } },
        null,
        2,
      );
    case 'claude-code':
    case 'cursor':
      return JSON.stringify({ mcpServers: { [serverName]: cursorServer } }, null, 2);
    case 'claude-desktop':
      return JSON.stringify(
        {
          mcpServers: {
            [serverName]: {
              command: 'npx',
              args: ['-y', '@modelcontextprotocol/inspector-cli', 'http', mcpUrl],
              env: { X_API_KEY: key },
            },
          },
        },
        null,
        2,
      );
    default:
      return `POST ${mcpUrl}\nHeaders:\n  x-api-key: ${key}`;
  }
}

export function downloadTextFile(name: string, content: string) {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5_000);
}

export function onboardingFilename(client: IdeClient, projectSlug?: string | null): string {
  const ide = IDE_META[client].id;
  return projectSlug
    ? `acm-${ide}-onboarding-${projectSlug}.md`
    : `acm-${ide}-onboarding.md`;
}

export interface OnboardingDocInput {
  mcpUrl: string;
  key: string;
  deeplink: string;
  snippet: string;
  ideLabel: string;
  configPath: string;
  when: string;
  protocolNote?: string;
  projectSlug?: string | null;
  projectId?: string | null;
}

/** Plain markdown. Callers wrap with i18n; this is the English/Czech-neutral skeleton used as t() default. */
export function buildOnboardingDocSkeleton(input: OnboardingDocInput): string {
  const protocol = input.protocolNote
    ? `\n## Project tool protocol\n${input.protocolNote}\n`
    : '';
  return `# ACM — ${input.ideLabel} MCP

Generated ${input.when} in the ACM dashboard.

## Fastest path
Open this link (${input.ideLabel} starts and fills in the config):

    ${input.deeplink}

## Manual config (${input.configPath})
\`\`\`json
${input.snippet}
\`\`\`

## Details
- **MCP endpoint:** ${input.mcpUrl}
- **API key:** \`${input.key}\`
  (carries your permissions — treat it like a password; ACM will not show the plaintext again)
${protocol}
## Steps
1. Open the deep-link above, OR paste the JSON into \`${input.configPath}\` and restart ${input.ideLabel}.
2. MCP: server should show as connected.
3. In chat try: “call acm tool toolbox.health_status”.

## Management
You can revoke the token anytime in ACM (profile → IDE / token list → Revoke).
`;
}

export interface IdePack {
  ide: IdeClient;
  deeplink: string;
  snippet: string;
  doc: string;
  filename: string;
}

export function buildIdePack(
  ide: IdeClient,
  mcpUrl: string,
  key: string,
  opts: {
    serverName?: string;
    projectSlug?: string | null;
    projectId?: string | null;
    protocolNote?: string;
    doc?: string;
  } = {},
): IdePack {
  const meta = IDE_META[ide];
  const serverName = opts.serverName ?? 'acm';
  const deeplink = buildDeeplink(ide, mcpUrl, key, serverName);
  const snippet = buildSnippet(ide, mcpUrl, key, serverName);
  const when = new Date().toISOString().slice(0, 16).replace('T', ' ');
  const doc = opts.doc ?? buildOnboardingDocSkeleton({
    mcpUrl,
    key,
    deeplink,
    snippet,
    ideLabel: meta.label,
    configPath: meta.configPath,
    when,
    protocolNote: opts.protocolNote,
    projectSlug: opts.projectSlug,
    projectId: opts.projectId,
  });
  return {
    ide,
    deeplink,
    snippet,
    doc,
    filename: onboardingFilename(ide, opts.projectSlug),
  };
}
